import { isDefined } from "@clipboard-health/util-ts";
import { getCommentUrl } from "@src/appV2/redesign/Workplace/Review/api/getCommentUrl";
import { type GetCommentResponse } from "@src/appV2/redesign/Workplace/Review/api/useGetComment";
import { getPaginatedWorkplaceCommentsQueryKey } from "@src/appV2/redesign/Workplace/Review/api/usePaginatedWorkplaceComments";
import {
  getWorkplaceCommentsQueryKey,
  type WorkplaceCommentsParams,
  type WorkplaceCommentsResponse,
  type WorkplaceCommentType,
} from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { type InfiniteData, type QueryClient } from "@tanstack/react-query";
import cloneDeep from "lodash/cloneDeep";

interface UpdateCommentListCacheParams {
  queryClient: QueryClient;
  workplaceId: string;
  commentsParams: WorkplaceCommentsParams;
  comment: WorkplaceCommentType;
}

interface DeleteCommentFromListCacheParams {
  queryClient: QueryClient;
  commentId: string;
  workplaceId: string;
  commentsParams: WorkplaceCommentsParams;
}

interface GetSingleCommentQueryKeyParams {
  workplaceId: string;
  commentId: string;
  commentsParams: WorkplaceCommentsParams;
}

function getSingleCommentQueryKey(params: GetSingleCommentQueryKeyParams) {
  const { workplaceId, commentId, commentsParams } = params;

  return [
    getCommentUrl({ workplaceId, commentId }),
    isDefined(commentsParams.viewContext) ? { viewContext: commentsParams.viewContext } : undefined,
  ];
}

/**
 * Helper function to update a comment in a list of comments
 */
function updateCommentInList<T extends WorkplaceCommentType[] | GetCommentResponse>(
  data: T | undefined,
  comment: WorkplaceCommentType
): T | undefined {
  if (!isDefined(data)) {
    return data;
  }

  const newData = cloneDeep(data);

  if (Array.isArray(newData)) {
    const commentIndex = newData.findIndex((oldComment) => oldComment.id === comment.id);
    if (commentIndex === -1) {
      return data;
    }

    newData[commentIndex] = comment;
  } else {
    newData.data = comment;
  }

  return newData;
}

function deleteCommentFromList<T extends WorkplaceCommentType[] | GetCommentResponse>(
  data: T | undefined,
  commentId: string
): T | undefined {
  if (!isDefined(data)) {
    return data;
  }

  const newData = cloneDeep(data);

  if (Array.isArray(newData)) {
    const commentIndex = newData.findIndex((oldComment) => oldComment.id === commentId);
    if (commentIndex === -1) {
      return data;
    }

    // remove the comment
    newData.splice(commentIndex, 1);
  } else if (newData.data?.id === commentId) {
    return undefined;
  }

  return newData;
}

/**
 * This updates the query data for the workplace comments.
 * It updates the workerReaction and the totalLikes count.
 * It might be a subject to change if we decide to use pagination for the comments.
 */
export function updateCommentListCache({
  queryClient,
  comment,
  commentsParams,
}: UpdateCommentListCacheParams) {
  queryClient.setQueryData<WorkplaceCommentType[]>(
    getWorkplaceCommentsQueryKey(commentsParams),
    (data) => updateCommentInList(data, comment)
  );
}

export function deletedCommentListCache({
  queryClient,
  commentId,
  commentsParams,
}: DeleteCommentFromListCacheParams) {
  queryClient.setQueryData<WorkplaceCommentType[]>(
    getWorkplaceCommentsQueryKey(commentsParams),
    (data) => deleteCommentFromList(data, commentId)
  );
}

function updateCommentInPaginatedList(
  data: InfiniteData<WorkplaceCommentsResponse> | undefined,
  comment: WorkplaceCommentType
) {
  if (!isDefined(data)) {
    return data;
  }

  const newData = cloneDeep(data);

  const pageIndex = newData.pages.findIndex((page) => page.data.some((c) => c.id === comment.id));

  if (pageIndex === -1) {
    return data;
  }

  const commentIndex = newData.pages[pageIndex].data.findIndex((c) => c.id === comment.id);

  if (commentIndex === -1) {
    return data;
  }

  newData.pages[pageIndex].data[commentIndex] = comment;

  return newData;
}

function deleteCommentFromPaginatedList(
  data: InfiniteData<WorkplaceCommentsResponse> | undefined,
  commentId: string
) {
  if (!isDefined(data)) {
    return data;
  }

  const newData = cloneDeep(data);

  const pageIndex = newData.pages.findIndex((page) => page.data.some((c) => c.id === commentId));

  if (pageIndex === -1) {
    return data;
  }

  const commentIndex = newData.pages[pageIndex].data.findIndex((c) => c.id === commentId);

  if (commentIndex === -1) {
    return data;
  }

  newData.pages[pageIndex].data.splice(commentIndex, 1);

  return newData;
}

export function updatePaginatedCommentListCache({
  queryClient,
  comment,
  commentsParams,
}: UpdateCommentListCacheParams) {
  const queryKey = getPaginatedWorkplaceCommentsQueryKey(commentsParams);

  queryClient.setQueryData<InfiniteData<WorkplaceCommentsResponse>>(queryKey, (data) =>
    updateCommentInPaginatedList(data, comment)
  );
}

export function deletePaginatedCommentListCache({
  queryClient,
  commentId,
  commentsParams,
}: DeleteCommentFromListCacheParams) {
  const queryKey = getPaginatedWorkplaceCommentsQueryKey(commentsParams);

  queryClient.setQueryData<InfiniteData<WorkplaceCommentsResponse>>(queryKey, (data) =>
    deleteCommentFromPaginatedList(data, commentId)
  );
}

/**
 * This updates the query data for the single comment.
 * It updates the workerReaction and the totalLikes count.
 */
export function updateSingleCommentCache({
  queryClient,
  comment,
  workplaceId,
  commentsParams,
}: UpdateCommentListCacheParams) {
  const queryKey = getSingleCommentQueryKey({
    workplaceId,
    commentId: comment.id,
    commentsParams,
  });

  queryClient.setQueryData<GetCommentResponse>(queryKey, (oldResponse) =>
    updateCommentInList(oldResponse, comment)
  );
}

export function deleteSingleCommentCache({
  queryClient,
  commentId,
  workplaceId,
  commentsParams,
}: DeleteCommentFromListCacheParams) {
  const queryKey = getSingleCommentQueryKey({ workplaceId, commentId, commentsParams });

  queryClient.removeQueries({ queryKey, exact: true });
}
